import { track as trackLandingView } from 'utils/signupLandingView';
import { useSignupPage, signupErrorReason } from 'utils/signupAnalytics';
import * as ls from 'utils/localStorage';
import React, { useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import GoogleRating, {
  DEFAULT_GOOGLE_RATING,
  DEFAULT_GOOGLE_REVIEWS_COUNT,
} from './components/GoogleRating';
import placeholderImage from '../../../images/JamesProfileSignUpPage.jpeg';
import logoBlue from '../../../images/logoBlue.svg';
import LandingIntroduction from './components/LandingIntroduction';
import SignUpForm from './components/SignUpForm';
import Api from '../../../api';
import { login, formatErrorMessage } from '../../../utils';
import { usePersistedState } from '../../../hooks';
import entities from '../../../reduxStore/entityKeys';
import useActionApi from '../../../hooks/useActionApi';
import FixedLoader from '../../../components/FixedLoader';
import { 
  trackSignUpStart, 
  captureUTMParameters,
  trackViewContent 
} from '../../../utils/facebookPixel';
import {
  trackSignUpStart as gtmTrackSignUpStart,
  captureUTMParameters as gtmCaptureUTMParameters,
  trackPageView as gtmTrackPageView
} from '../../../utils/gtmDataLayer';

import {
    LandingContainer,
    FormContainer,
    ContentWrapper,
    ImageContainer,
    MobileImage,
    Logo,
    TopSection,
    BottomSection,
    SectionDivider,
      SignInWrapper,
      SignInText,
      SignInLink,
} from './Landing.styles';

interface IFormData {
    email: string;
    password: string;
    referralCode: string;
    terms: boolean;
}

function canCountSignupView() {
  const legacy = (window as any).TempStars;
  return !legacy?.User?.getCurrentUser?.()
    && (!ls.get('userAuth') || legacy?.App?.isAuthBootstrapSettled?.() === true);
}

export default function Landing() {
  const history = useHistory();
  const [signupViewEnabled, setSignupViewEnabled] = React.useState(canCountSignupView);
  // Legacy auto-login temporarily clears its in-memory user. Wait for its
  // outcome so a restored session is excluded and a failed session can sign up.
  const signup = useSignupPage('create_account', 'unknown', undefined,
    signupViewEnabled);
  useEffect(() => {
    const update = () => setSignupViewEnabled(canCountSignupView());
    window.addEventListener('tempstars-auth-bootstrap-settled', update);
    update(); // Bootstrap may finish between the first render and this listener.
    return () => window.removeEventListener('tempstars-auth-bootstrap-settled', update);
  }, []);
  const location = useLocation<{ refCode?: string }>();
  const [apiError, setApiError] = React.useState('');
  const [referralCodeError, setReferralCodeError] = React.useState('');
  const [googleReviewsCount, setGoogleReviewsCount] = React.useState(
    DEFAULT_GOOGLE_REVIEWS_COUNT,
  );
  // T2-5090: when the user arrives via a referral share link, pre-fill the "Have a Referral
  // Code?" field. The code arrives either as router state (`refCode`, set when the F7 shell
  // routes a ?ref= deep link via goReact — goReact reloads index.html and drops the query
  // string) or directly in the URL query (`?ref=` / `?refCode=` on a plain web load).
  const initialReferralCode = React.useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return (location.state?.refCode || params.get('ref') || params.get('refCode') || '').trim();
  }, [location.state]);
  const setUser = usePersistedState({
		entityName: entities.user,
	})[1];
    // @ts-ignore
    const TempStars: any = window.TempStars;

  const [creatingUserLoading, , createUserFlow] = useActionApi<IFormData, { userId: number}>({
    apiMethod: async (data: IFormData) => {
      const userByEmail = await TempStars.Api.findUserByEmail(data.email);
      if (userByEmail.isFound) {
        throw new Error('User with this email already exists');
      }

      const { userId } = await Api.createUser({
        email: data.email,
        password: data.password,
        affiliateCode: data.referralCode,
      });

      // T2-4512: Identity stitching - alias then identify BEFORE login
      TempStars.Analytics.alias(String(userId));
      TempStars.Analytics.identify(String(userId));
      
      await login(data.email, data.password, setUser);
      
      // T2-4512: Set UTM last-touch properties AFTER identify
      TempStars.User.setAnalytics();
      
      await Api.updateToken();
      
      // T2-4512: Track create account event AFTER identity is established
      TempStars.Analytics.track('sign-up - create account');

      return { userId };
    },
    onSuccess: ({ userId }) => {
      signup.submitted();
      history.push({
        pathname: 'RoleSelect',
        state: { userId },
      });
    },
    onError: (err: any) => {
      signup.error(signupErrorReason(err));
      if (signupErrorReason(err) === 'duplicate_account') signup.duplicate();
      // Check for referral code specific errors
      try {
        const parsed = JSON.parse(err?.message);
        const code = parsed?.error?.code;
        if (code === 'INVALID_REFERRAL_CODE' || code === 'EXPIRED_REFERRAL_CODE') {
          setReferralCodeError(parsed?.error?.message || 'Invalid referral code');
          return;
        }
      } catch {
        // Not JSON, fall through to generic error
      }
      setApiError(formatErrorMessage(err) || 'An unexpected error occurred. Please try again.');
    },
  });

  const onSubmit = async (data: IFormData) => {
    setApiError('');
    setReferralCodeError('');
    TempStars.Analytics.track('sign-up - submission started', { role: 'unselected', signup_flow: 'landing' });
    
    // Track sign-up initiation BEFORE account creation
    // This is the true top of funnel - user has intent to sign up
    
    // Facebook Pixel tracking (existing)
    trackSignUpStart();
    
    // GTM dataLayer tracking (new - mirrors Facebook Pixel)
    gtmTrackSignUpStart();
    
    await createUserFlow(data);
  };

  const goLogin = (e: React.MouseEvent) => {
    e.preventDefault();
    history.push({
      pathname: 'Login',
    });
  };

  useEffect(() => {
    if (signupViewEnabled) trackLandingView(TempStars.Analytics, TempStars.User?.getCurrentUser?.());
  }, [signupViewEnabled]);

  useEffect(() => {
    let isActive = true;

    void Api.getGoogleReviews()
      .then(({ totalReviews }) => {
        if (isActive && Number.isInteger(totalReviews) && totalReviews > 0) {
          setGoogleReviewsCount(totalReviews);
        }
      })
      .catch(() => {
        // Keep the visible 812 fallback when the live Google lookup is unavailable.
      });

    return () => {
      isActive = false;
    };
  }, []);
  
  // Capture UTM parameters and track page view on mount
  useEffect(() => {
    // Capture UTM parameters for later use in conversion events
    // NOTE: Both Facebook Pixel and GTM use the same sessionStorage key
    captureUTMParameters();
    gtmCaptureUTMParameters(); // Also capture for GTM (uses same storage)
    
    // Track landing page view
    // Facebook Pixel (existing)
    trackViewContent('Landing Page', 'Sign Up Flow');
    
    // GTM dataLayer (new)
    gtmTrackPageView(window.location.pathname, 'Landing Page - Sign Up Flow');
  }, []);

  if (window.location.search.indexOf('resetPasswordToken') !== -1) {
    history.push({
        pathname: 'ResetPassword'
    });
}

  return (
    <LandingContainer>
      <FormContainer>
        <ContentWrapper>
          <TopSection>
            <Logo src={logoBlue} alt="TempStars Logo" />
            <LandingIntroduction />
          </TopSection>
          
          <SectionDivider />
          
          <BottomSection>
            <SignUpForm onSubmit={onSubmit} apiError={apiError} referralCodeError={referralCodeError} initialReferralCode={initialReferralCode} />
            <SignInWrapper>
              <SignInText variant="body1">
                Already have an account? <SignInLink onClick={goLogin} className="no-fastclick">Sign in</SignInLink>
              </SignInText>
            </SignInWrapper>
            <GoogleRating rating={DEFAULT_GOOGLE_RATING} reviews={googleReviewsCount} />
          </BottomSection>
        </ContentWrapper>
        <MobileImage src={placeholderImage} alt="Founder" />
      </FormContainer>
      <ImageContainer />
      <FixedLoader loading={creatingUserLoading} />
    </LandingContainer>
  );
}
